import { BuildOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import type { MouseEvent } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import styled, { css } from 'styled-components';

import { CenterFlex, Divider, Flex, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Empty from '@/components/Empty/Empty';
import List from '@/components/List/List';
import { MainText } from '@/components/Typography/NamedTypo';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import {
  TOPIC_TREND_CATEGORY,
  TOPIC_TREND_CLIENT,
} from '@/features/GlobalSearch/types/searchConst';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { useToast } from '@/stores/pure/ToastStore';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { GPT_DOMAIN } from '@/types/gptConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import SearchContentStyles from '../SearchContentStyles';
import SearchDrilldownMenu from '../SearchDrilldownMenu';
import TopicTrendItem from './TopicTrendItem';

type SelectedTopic = {
  alias: string;
  name: string;
} | null;

const { ContentHeader } = SearchContentStyles;

const TopicTrend = () => {
  return (
    <ErrorBoundary
      fallbackRender={() => <MainText dim>Error occurred</MainText>}
    >
      <Content />
    </ErrorBoundary>
  );
};

const Content = () => {
  const { alias, searchResult } = useSearchDetailContext();
  const wr = useWorkspaceRouter();
  const { closeModal, toDetailPage, refreshHistoryList } =
    useGlobalSearchModalAction();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const addToast = useToast();
  const { state, setState } = useStates<TopicTrendRequestDTO>({
    category: TOPIC_TREND_CATEGORY.VOLUME,
    client: null,
    level: null,
  });

  const { status, open, close, pos, setPos, modalState, setModalState } =
    useModalState<SelectedTopic>(null);

  const topicQuery = useQuery({
    queryKey: [
      'search-topic-trend',
      alias,
      state.category,
      state.client,
      state.level,
    ],
    queryFn: () =>
      service.globalSearch.fetchTopicTrend(alias, {
        category: state.category,
        client: state.client,
        level: state.level,
      }),
  });

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!modalState) return;

    const filterPayload = {
      [FEEDBACK_COLUMNS.TAXONOMY_TOPIC]: structuredFilterUtils.buildSingleValue(
        modalState.alias,
      ),
    };

    setNewModal<FeedbackBlockListModalOptions>({
      alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: ['topic', modalState.alias],
      options: {
        queryKeys: [modalState.alias],
        title: 'Feedback list',
        subTitle: `Topic: ${modalState.name}`,
        domain: 'global-search',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus.sortBy,
                order: sortStatus.orderStatus,
              },
              payload: filterPayload,
            },
          );

          const PAGE_SIZE = 50;

          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();

    if (!modalState) return;

    const hasTaxonomyFilter =
      searchResult.filter?.[FEEDBACK_COLUMNS.TAXONOMY_TOPIC];
    if (!Array.isArray(hasTaxonomyFilter))
      throw new Error('Taxonomy filter should be array');
    const newFilter = new SFilter(searchResult.filter);

    if (hasTaxonomyFilter) {
      if (
        hasTaxonomyFilter.length > 1 &&
        hasTaxonomyFilter.find((f) => f.op === STRUCTURED_FILTER_OPERATOR.OR)
      ) {
        // 이미 taxonomy 필터를 갖고 있고 OR 연산자인 경우 필터 추가 불가능 안내
        addToast('Topic filter can not be multiple', 'fail');
        return;
      }
    }

    newFilter.addValueSet(FEEDBACK_COLUMNS.TAXONOMY_TOPIC, {
      op: STRUCTURED_FILTER_OPERATOR.OR,
      values: [modalState.alias],
    });

    const res = await service.globalSearch.submitSearch({
      title: `${searchResult.title} copy`,
      filter: newFilter.get(),
      query: searchResult.query,
      options: {
        ...searchResult.options,
      },
    });

    toDetailPage(res.alias);
    refreshHistoryList();
  };

  const onClickHandler = (
    e: MouseEvent,
    topic: { alias: string; name: string },
  ) => {
    setPos({
      top: e.clientY,
      left: e.clientX,
    });
    setModalState(topic);
    open();
  };

  return (
    <Style g={24}>
      <Flex j="space-between">
        <Flex a="center" g={16}>
          <ContentHeader>Related topics</ContentHeader>
          <Flex a="center">
            <Select
              style={{ width: 120 }}
              size="small"
              value={state.level}
              onChange={(value) => {
                setState({ level: value });
              }}
              options={[
                {
                  label: 'All levels',
                  value: null,
                },
                {
                  label: 'Level 1',
                  value: 1,
                },
                {
                  label: 'Level 2',
                  value: 2,
                },
                {
                  label: 'Level 3',
                  value: 3,
                },
                {
                  label: 'Level 4',
                  value: 4,
                },
              ]}
            />
          </Flex>
        </Flex>
        <Flex g={8}>
          {state.category === TOPIC_TREND_CATEGORY.REVENUE_IMPACT && (
            <Select
              style={{ width: 120 }}
              size="small"
              value={state.client}
              onChange={(value) => {
                setState({ client: value });
              }}
              options={[
                {
                  label: 'Accounts',
                  value: TOPIC_TREND_CLIENT.ACCOUNT,
                },
                {
                  label: 'Contacts',
                  value: TOPIC_TREND_CLIENT.CONTACT,
                },
              ]}
            />
          )}
          <Select
            style={{ width: 'auto' }}
            dropdownStyle={{ width: 200 }}
            size="small"
            value={state.category}
            onChange={(value) => {
              if (value === TOPIC_TREND_CATEGORY.REVENUE_IMPACT) {
                setState({
                  client: TOPIC_TREND_CLIENT.ACCOUNT,
                  category: value,
                });
              } else {
                setState({ category: value, client: null });
              }
            }}
            options={[
              {
                label: 'Volume',
                value: TOPIC_TREND_CATEGORY.VOLUME,
              },
              {
                label: 'Negative share',
                value: TOPIC_TREND_CATEGORY.NEGATIVE_RATIO,
              },
              {
                label: 'Revenue impact (SUM)',
                value: TOPIC_TREND_CATEGORY.REVENUE_IMPACT,
              },
            ]}
          />
        </Flex>
      </Flex>
      <List
        className="grid grid-flow-col grid-cols-2 grid-rows-5 gap-x-8 gap-y-1 min-h-[310px]"
        data={topicQuery.data?.slice(0, 10)}
        isLoading={topicQuery.isFetching}
        placeholder={{
          node: (
            <CenterFlex
              css={css`
                grid-column: 1 / span 2;
                grid-row: 1 / span 5;
              `}
            >
              <Empty
                icon={<BuildOutlined />}
                title="No topics to display yet"
                description={
                  <>
                    Head over to Taxonomy and create a topic to start viewing
                    insights here.
                    <br />
                    Identify topics related to your search results for clearer
                    insights.
                  </>
                }
                className="h-80"
                action={{
                  onClick: () => {
                    closeModal();
                    wr.push('/taxonomy');
                  },
                  text: 'Set up taxonomy to view topics',
                  option: 'ghost',
                }}
              />
            </CenterFlex>
          ),
        }}
        loader={
          <CenterFlex
            style={{ gridColumn: '1 / span 2', gridRow: '1 / span 5' }}
          >
            <Spinner />
          </CenterFlex>
        }
        render={(topic, index) => {
          return (
            <VFlex key={topic.alias} g={4}>
              <TopicTrendItem
                topic={topic}
                category={state.category}
                maxValue={topicQuery.data?.[0]?.value ?? 0}
                onClick={(e) => {
                  e.preventDefault();
                  onClickHandler(e, {
                    alias: topic.alias,
                    name: topic.name,
                  });
                }}
              />
              {(index + 1) % 5 !== 0 && <Divider />}
            </VFlex>
          );
        }}
      />
      <SearchDrilldownMenu
        pos={{
          top: pos?.top as number,
          left: pos?.left as number,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={onViewFeedbackListHandler}
        onAddToFilterClick={onAddToFilterHandler}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </Style>
  );
};

const Style = styled(VFlex)`
  padding: 24px;
`;
export default TopicTrend;
